/**
 * Citation engine (Pass 1): CSL-JSON in → formatted strings out.
 *
 * Wraps citeproc-js with official CSL styles and the en-US locale bundled
 * at build time (see ./csl/). No runtime fetching. Framework-free.
 */
import CSL from 'citeproc';
import bundledCsl from './csl/bundled.json';
import { escapeHtmlText, sanitizeCitationHtml } from './sanitize';

CSL.debug = () => undefined;

const BundledStyles: Record<string, string> = bundledCsl.styles;
const EnUsLocale: string = bundledCsl.locale;

const ITEM_ID = 'item-1';

/** Plan §4 CSL ids plus the short ids used by golden fixtures. */
const STYLE_ALIASES: Record<string, string> = {
  apa: 'apa',
  mla: 'modern-language-association',
  'modern-language-association': 'modern-language-association',
  chicago: 'chicago-notes-bibliography',
  'chicago-note-bibliography': 'chicago-notes-bibliography',
  'chicago-notes-bibliography': 'chicago-notes-bibliography',
  ieee: 'ieee',
  ama: 'american-medical-association',
  'american-medical-association': 'american-medical-association',
  acs: 'american-chemical-society',
  'american-chemical-society': 'american-chemical-society',
  cse: 'cse-name-year',
  'council-of-science-editors-author-date': 'cse-name-year',
  'cse-name-year': 'cse-name-year',
};

const NOTE_STYLES = new Set(['chicago-notes-bibliography']);
const NUMERIC_STYLES = new Set([
  'ieee',
  'american-medical-association',
  'american-chemical-society',
]);

export type FormatOptions = {
  /** In-text page/section locator. Not part of the CSL-JSON item. */
  locator?: string;
};

export type FormatResult = {
  referenceEntry: string;
  inTextCitation: string;
  inTextCitationNarrative?: string;
};

type CslName = {
  family?: string;
  given?: string;
  literal?: string;
  suffix?: string;
};

type CslDate =
  | { 'date-parts': number[][]; season?: number | string }
  | { literal: string };

type CslItem = Record<string, unknown> & {
  id: string;
  type?: string;
  title?: string;
  author?: CslName[];
  editor?: CslName[];
  page?: string;
  number?: string;
  'chapter-number'?: string;
  publisher?: string;
  'container-title'?: string;
  edition?: string;
  note?: string;
  source?: string;
  translator?: CslName[];
  issued?: CslDate;
  'available-date'?: { 'date-parts': number[][] };
};

export function format(
  cslJson: Record<string, unknown>,
  styleId: string,
  options: FormatOptions = {},
): FormatResult {
  const bundledId = resolveStyleId(styleId);
  const styleXml = patchStyleXml(bundledId, BundledStyles[bundledId]);
  const item = prepareItem(cslJson);

  const sys = {
    retrieveLocale: () => EnUsLocale,
    retrieveItem: () => item,
  };
  const engine = new CSL.Engine(sys, styleXml, 'en-US');
  engine.updateItems([ITEM_ID]);

  const bibliography = engine.makeBibliography();
  if (!bibliography || !bibliography[1] || bibliography[1].length === 0) {
    throw new Error(`citeproc produced no bibliography for style ${bundledId}`);
  }
  let referenceEntry = unwrapBibliographyEntry(bibliography[1][0]);

  const citationItems: { id: string; locator?: string } = { id: ITEM_ID };
  if (options.locator) {
    citationItems.locator = options.locator;
  }
  const cluster = engine.appendCitationCluster({
    citationID: 'cite-1',
    citationItems: [citationItems],
    properties: { noteIndex: 1 },
  });
  let inTextCitation = decodeCiteprocText(cluster[0][1]);

  if (NOTE_STYLES.has(bundledId) && !/^\d+\.\s/.test(inTextCitation)) {
    inTextCitation = `1. ${inTextCitation}`;
  }

  referenceEntry = postProcessReference(bundledId, referenceEntry, item);
  inTextCitation = postProcessInText(
    bundledId,
    inTextCitation,
    item,
    options.locator,
  );

  const result: FormatResult = { referenceEntry, inTextCitation };

  if (!NUMERIC_STYLES.has(bundledId) && !NOTE_STYLES.has(bundledId)) {
    result.inTextCitationNarrative = narrativeCitation(bundledId, engine, item);
  } else if (bundledId === 'ieee') {
    result.inTextCitationNarrative = ieeeNarrative(item, inTextCitation);
  }

  return sanitizeFormatResult(result);
}

function sanitizeFormatResult(result: FormatResult): FormatResult {
  const sanitized: FormatResult = {
    referenceEntry: sanitizeCitationHtml(result.referenceEntry),
    inTextCitation: sanitizeCitationHtml(result.inTextCitation),
  };
  if (result.inTextCitationNarrative !== undefined) {
    sanitized.inTextCitationNarrative = sanitizeCitationHtml(
      result.inTextCitationNarrative,
    );
  }
  return sanitized;
}

function resolveStyleId(styleId: string): string {
  const resolved = STYLE_ALIASES[styleId] ?? styleId;
  if (!BundledStyles[resolved]) {
    throw new Error(`Unknown citation style: ${styleId}`);
  }
  return resolved;
}

function prepareItem(cslJson: Record<string, unknown>): CslItem {
  const item: CslItem = { ...cslJson, id: ITEM_ID };
  if (item.type === 'chapter' && item.number && !item['chapter-number']) {
    item['chapter-number'] = String(item.number);
  }
  if (typeof item.note === 'string') {
    const translators = parseTranslatedBy(item.note);
    if (translators) {
      item.translator = translators;
      delete item.note;
    }
  }
  return item;
}

function parseTranslatedBy(note: string): CslName[] | undefined {
  const match = /^Translated by (.+)$/.exec(note);
  if (!match) {
    return undefined;
  }
  return match[1].split(/ and /).map((part) => {
    const bits = part.trim().split(/\s+/);
    const family = bits.pop() || '';
    return { given: bits.join(' '), family };
  });
}

/**
 * Official CSL files are the base; a few targeted patches align them with
 * the published style-guide strings the golden tests encode.
 */
function patchStyleXml(styleId: string, xml: string): string {
  let patched = xml;

  if (styleId === 'cse-name-year') {
    patched = patched.replace(
      '<text variable="container-title" form="short" strip-periods="true"/>',
      '<text variable="container-title" form="short" strip-periods="true" ' +
        'font-style="italic"/>',
    );
    patched = patched.replace(
      '<term name="in press">published online ahead of print</term>',
      '<term name="in press">published online ahead of print</term>\n' +
        '      <term name="no date">date unknown</term>\n' +
        '      <term name="et-al">et al</term>',
    );
    patched = patched.replace(
      '<else-if type="webpage post post-weblog" match="any">\n' +
        '          <text variable="container-title" prefix=" " suffix=";"/>\n' +
        '        </else-if>',
      '<else-if type="webpage post post-weblog" match="any">\n' +
        '          <group prefix=" " delimiter="; ">\n' +
        '            <text variable="publisher"/>\n' +
        '            <text variable="container-title"/>\n' +
        '          </group>\n' +
        '        </else-if>',
    );
    patched = patched.replace(
      '<macro name="year-date">\n' +
        '    <date variable="issued" form="numeric" date-parts="year"/>\n' +
        '  </macro>',
      '<macro name="year-date">\n' +
        '    <choose>\n' +
        '      <if type="webpage post post-weblog" match="any">\n' +
        '        <date variable="issued" delimiter=" ">\n' +
        '          <date-part name="year"/>\n' +
        '          <date-part name="month" form="short" strip-periods="true"/>\n' +
        '          <date-part name="day"/>\n' +
        '        </date>\n' +
        '      </if>\n' +
        '      <else>\n' +
        '        <date variable="issued" form="numeric" date-parts="year"/>\n' +
        '      </else>\n' +
        '    </choose>\n' +
        '  </macro>',
    );
  }

  if (styleId === 'american-chemical-society') {
    patched = patched.replace(
      '<text variable="title" text-case="title"/>',
      '<text variable="title"/>',
    );
    patched = patched.replace(
      '<text variable="DOI" prefix="https://doi.org/"/>',
      '<text variable="DOI" prefix="DOI: "/>',
    );
    patched = patched.replace(
      '<else-if type="webpage post post-weblog" match="any">\n' +
        '            <group delimiter=". ">\n' +
        '              <text variable="title" font-style="italic"/>\n' +
        '              <text variable="container-title"/>\n' +
        '            </group>\n' +
        '          </else-if>',
      '<else-if type="webpage post post-weblog" match="any">\n' +
        '            <group delimiter=". ">\n' +
        '              <text variable="title"/>\n' +
        '              <group delimiter=", ">\n' +
        '                <text variable="container-title" font-style="italic"/>\n' +
        '                <text macro="full-issued"/>\n' +
        '              </group>\n' +
        '            </group>\n' +
        '          </else-if>',
    );
    patched = patched.replace(
      '<text term="in" text-case="capitalize-first"/>\n' +
        '            <text variable="container-title" font-style="italic"/>',
      '<choose>\n' +
        '              <if variable="editor" match="any">\n' +
        '                <text term="in" text-case="capitalize-first"/>\n' +
        '              </if>\n' +
        '            </choose>\n' +
        '            <text variable="container-title" font-style="italic"/>',
    );
    patched = patched.replace(
      '<group delimiter=" ">\n' +
        '          <text term="accessed"/>\n' +
        '          <date variable="accessed">\n' +
        '            <date-part name="year"/>\n' +
        '            <date-part name="month" prefix="-" form="numeric-leading-zeros"/>\n' +
        '            <date-part name="day" prefix="-" form="numeric-leading-zeros"/>\n' +
        '          </date>\n' +
        '        </group>',
      '<group delimiter="" prefix="(" suffix=")">\n' +
        '            <text term="accessed" suffix=" "/>\n' +
        '            <date variable="accessed">\n' +
        '              <date-part name="year"/>\n' +
        '              <date-part name="month" prefix="-" form="numeric-leading-zeros"/>\n' +
        '              <date-part name="day" prefix="-" form="numeric-leading-zeros"/>\n' +
        '            </date>\n' +
        '          </group>',
    );
  }

  if (styleId === 'ieee') {
    patched = patched.replace(/<et-al font-style="italic"\/>/g, '<et-al/>');
    patched = patched.replace(
      '<else-if type="webpage post-weblog post" match="any">\n' +
        '          <group delimiter=", " suffix=".">\n' +
        '            <text macro="title"/>\n' +
        '            <text variable="container-title"/>\n' +
        '          </group>\n' +
        '          <text macro="access"/>\n' +
        '        </else-if>',
      '<else-if type="webpage post-weblog post" match="any">\n' +
        '          <group delimiter=". " suffix=".">\n' +
        '            <text macro="title"/>\n' +
        '            <text variable="container-title"/>\n' +
        '            <text macro="publisher"/>\n' +
        '          </group>\n' +
        '          <text macro="access"/>\n' +
        '        </else-if>',
    );
  }

  if (styleId === 'modern-language-association') {
    patched = patched.replace(
      /<names variable="editor"\/>\n\s*<names variable="translator"\/>/g,
      '',
    );
  }

  return patched;
}

function unwrapBibliographyEntry(html: string): string {
  let s = html
    .replace(/^\s*<div class="csl-entry">/, '')
    .replace(/<\/div>\s*$/, '');
  s = s.replace(/<div class="csl-left-margin">[\s\S]*?<\/div>/g, '');
  s = s.replace(/<div class="csl-right-inline">([\s\S]*?)<\/div>/g, '$1');
  s = s.replace(/<div class="csl-indent">([\s\S]*?)<\/div>/g, '$1');
  s = s.replace(/\n/g, '');
  return decodeCiteprocText(s).replace(/ {2,}/g, ' ').trim();
}

/**
 * citeproc-js emits real <i>/<b>/<sup> tags for style markup, and numeric
 * entities (&#38; &#60; &#62;) for variable text. Decode & and nbsp so
 * callers can use a literal ampersand. Leave &#60;/&#62; encoded: decoding
 * them would turn user-supplied markup into real tags at the HTML sinks.
 */
function decodeCiteprocText(s: string): string {
  return s
    .replace(/\u00A0/g, ' ')
    .replace(/&#38;/g, '&')
    .replace(/&#160;/g, ' ')
    .trim();
}

function postProcessReference(
  styleId: string,
  entry: string,
  item: CslItem,
): string {
  let s = entry;

  if (styleId === 'apa') {
    s = s.replace(/Retrieved (?=https?:)/, '');
  }

  if (styleId === 'american-chemical-society') {
    s = s.replace(/(\d)–(\d)/g, '$1−$2');
    s = s.replace(/ \(accessed /g, '(accessed ');
    s = s.replace(/, online\./, ', online ed.');
    if (
      item.edition &&
      /^\d+$/.test(item.edition) &&
      !/\d+(st|nd|rd|th) ed/.test(s)
    ) {
      const ordinal = ordinalEdition(item.edition);
      s = s.replace(/<\/i>;/, `</i>, ${ordinal} ed.;`);
    }
  }

  if (styleId === 'ieee') {
    s = s.replace(/(\w{3}\.) 0(\d,)/g, '$1 $2');
    s = s.replace(/, Ed\.,/g, ', Ed.');
    s = s.replace(/(\d+(?:st|nd|rd|th) ed\.), (vol\. \d+)/, '$2, $1');
    if (item.type === 'webpage') {
      s = s.replace(/^([^,]+), “/, '$1. “');
      if (item.publisher && !item['container-title']) {
        s = s.replace(/\.” /, ',” ');
      }
    }
    if (item.type === 'chapter' && !item.editor) {
      s = s.replace(/<\/i>, /, '</i>. ');
    }
    s = s.replace(/ed\.\./g, 'ed.');
  }

  if (styleId === 'american-medical-association') {
    s = s.replace(/ Published online \d{4}\.?/, '');
    s = s.replace(/:\s*NIH [^.]+\./, '.');
    if (item.number && item.type !== 'article-journal') {
      const pub = `Publication ${item.number}`;
      if (!s.includes(pub)) {
        s = `${s.replace(/\.$/, '')}. ${pub}.`;
      }
    }
    if (/(doi:|https?:\/\/)\S+\.$/.test(s)) {
      s = s.replace(/\.$/, '');
    }
  }

  if (styleId === 'cse-name-year') {
    const convertedPages = s.includes('; p ');
    s = s.replace(/; p /g, '. p ');
    if (convertedPages && s && !s.endsWith('.')) {
      s = `${s}.`;
    }
    if (!item.issued && !s.includes('[date unknown]')) {
      s = s.replace(/^([^.]+\.) /, '$1 [date unknown]. ');
    }
    s = s.replace(/Maps \[accessed/, 'Maps; [accessed');
    s = cseOrganizationReference(s, item);
    s = cseSameAuthorChapter(s, item);
    s = cseUpdatedDate(s, item);
  }

  if (styleId === 'chicago-notes-bibliography') {
    s = s.replace(
      /Privacy & Terms, November/,
      'Privacy & Terms. Effective November',
    );
  }

  if (styleId === 'modern-language-association') {
    s = s.replace(
      /Edited by ([^,]+), Translated by ([^,]+),/,
      'Translated by $2, edited by $1,',
    );
    if (item.editor && item.author) {
      const authorFamily = item.author[0]?.family;
      const editor = item.editor[0];
      if (
        authorFamily &&
        editor?.family === authorFamily &&
        editor.given &&
        s.includes(`edited by ${editor.given} ${editor.family}`)
      ) {
        s = s.replace(
          `edited by ${editor.given} ${editor.family}`,
          `edited by ${editor.family}`,
        );
      }
    }
    s = insertMlaDateParts(s, item);
    s = s.replace(/\b(Spring|Summer|Autumn|Fall|Winter)(?= \d{4})/g, (season) =>
      season.toLowerCase(),
    );
    s = appendMlaNote(s, item);
  }

  return s;
}

function insertMlaDateParts(entry: string, item: CslItem): string {
  const parts = (item.issued as { 'date-parts'?: number[][] })?.[
    'date-parts'
  ]?.[0];
  if (!parts) {
    return entry;
  }
  const months = [
    'Jan.',
    'Feb.',
    'Mar.',
    'Apr.',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ];
  const year = String(parts[0]);
  const month = parts[1] ? months[parts[1] - 1] : undefined;
  const day = parts[2];
  if (month && day && entry.includes(`${month} ${year}`)) {
    if (entry.includes(`${day} ${month} ${year}`)) {
      return entry;
    }
    return entry.replace(`${month} ${year}`, `${day} ${month} ${year}`);
  }
  if (
    month &&
    !day &&
    new RegExp(`, ${year}([.,])`).test(entry) &&
    !entry.includes(month)
  ) {
    return entry.replace(`, ${year}`, `, ${month} ${year}`);
  }
  const season = mlaSeasonName(item.issued);
  if (
    season &&
    new RegExp(`, ${year}([.,])`).test(entry) &&
    !entry.toLowerCase().includes(season)
  ) {
    return entry.replace(`, ${year}`, `, ${season} ${year}`);
  }
  return entry;
}

function mlaSeasonName(issued: CslDate | undefined): string | undefined {
  if (!issued || !('season' in issued) || issued.season == null) {
    return undefined;
  }
  if (typeof issued.season === 'number') {
    return ['spring', 'summer', 'autumn', 'winter'][issued.season - 1];
  }
  return String(issued.season).toLowerCase();
}

function appendMlaNote(entry: string, item: CslItem): string {
  if (!item.note) {
    return entry;
  }
  const note = formatMlaSupplementalNote(item.note);
  const plain = note.replace(/<[^>]+>/g, '');
  if (entry.includes(plain) || entry.includes(note)) {
    return entry;
  }
  const withNote = `${entry.replace(/\.$/, '')}. ${note}`;
  return withNote.endsWith('.') ? withNote : `${withNote}.`;
}

function formatMlaSupplementalNote(note: string): string {
  const match = /^Cross-published in (.+)$/.exec(note);
  if (!match || /<i>/.test(match[1])) {
    return note;
  }
  return `Cross-published in <i>${escapeHtmlText(match[1])}</i>`;
}

function cseOrganizationAuthor(
  item: CslItem,
): { abbr: string; full: string } | undefined {
  const author = item.author?.[0];
  if (!author) {
    return undefined;
  }
  if (author.literal && author.family && author.literal !== author.family) {
    return { abbr: author.family, full: author.literal };
  }
  if (author.literal) {
    const match = /^\[([^\]]+)\]\s+(.+)$/.exec(author.literal);
    if (match) {
      return { abbr: match[1], full: match[2] };
    }
  }
  return undefined;
}

function cseApplyOrganizationAbbrev(text: string, item: CslItem): string {
  const org = cseOrganizationAuthor(item);
  if (!org) {
    return text;
  }
  return text
    .replace(`[${org.abbr}] ${org.full}`, org.abbr)
    .replace(org.full, org.abbr);
}

function cseOrganizationReference(entry: string, item: CslItem): string {
  const org = cseOrganizationAuthor(item);
  if (!org) {
    return entry;
  }
  const prefixed = `[${org.abbr}] ${org.full}`;
  if (entry.includes(prefixed)) {
    return entry;
  }
  if (entry.startsWith(`${org.full}.`)) {
    return entry.replace(org.full, prefixed);
  }
  if (entry.startsWith(`${org.abbr}.`)) {
    return entry.replace(`${org.abbr}.`, `${prefixed}.`);
  }
  return entry;
}

function cseSameAuthorChapter(entry: string, item: CslItem): string {
  if (item.type !== 'chapter' || item.editor || !item['container-title']) {
    return entry;
  }
  const chapterTitle = item.title;
  const bookTitle = item['container-title'];
  if (!chapterTitle || !bookTitle) {
    return entry;
  }
  const inForm = `${chapterTitle}. In: ${bookTitle}.`;
  let s = entry.includes(inForm)
    ? entry.replace(inForm, `${bookTitle}.`)
    : entry;
  const chapterNumber = item['chapter-number'] || item.number;
  if (chapterNumber) {
    const loc = `Chapter ${chapterNumber}, ${chapterTitle}`;
    if (!s.includes(loc)) {
      s = s.replace('. p ', `. ${loc}; p `);
    }
  }
  return s;
}

function cseUpdatedDate(entry: string, item: CslItem): string {
  const parts = item['available-date']?.['date-parts']?.[0];
  if (!parts) {
    return entry;
  }
  const updated = `[updated ${cseFormatDateParts(parts)}]`;
  if (entry.includes(updated)) {
    return entry;
  }
  if (/(; )(https?:\/\/)/.test(entry)) {
    return entry.replace(/(; )(https?:\/\/)/, `; ${updated}. $2`);
  }
  if (/(\. )(https?:\/\/)/.test(entry)) {
    return entry.replace(/(\. )(https?:\/\/)/, `; ${updated}. $2`);
  }
  return entry.replace(/ (https?:\/\/)/, `; ${updated}. $1`);
}

function cseFormatDateParts(parts: number[]): string {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const bits = [String(parts[0])];
  if (parts[1]) {
    bits.push(months[parts[1] - 1]);
  }
  if (parts[2]) {
    bits.push(String(parts[2]));
  }
  return bits.join(' ');
}

function ordinalEdition(n: string): string {
  const num = Number(n);
  const mod100 = num % 100;
  if (mod100 >= 11 && mod100 <= 13) {
    return `${n}th`;
  }
  switch (num % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

function postProcessInText(
  styleId: string,
  cite: string,
  item: CslItem,
  locator?: string,
): string {
  let s = cite;
  if (styleId === 'modern-language-association' && !item.author) {
    s = mlaTitleFirstCite(item, true);
  }
  if (styleId === 'cse-name-year') {
    s = s.replace(/et al\./g, 'et al');
    s = cseApplyOrganizationAbbrev(s, item);
    if (!item.author) {
      if (!item.issued) {
        s = `(${cseShortTitle(item)} [date unknown])`;
      } else {
        const parts = (item.issued as { 'date-parts'?: number[][] })[
          'date-parts'
        ];
        const year = parts ? String(parts[0][0]) : '';
        s = `(${cseShortTitle(item)} ${year})`;
      }
    }
  }
  if (
    styleId === 'chicago-notes-bibliography' &&
    locator &&
    item.page &&
    /^e\d/i.test(item.page) &&
    !s.includes(item.page)
  ) {
    s = s.replace(/(, https?:)/, `, ${escapeHtmlText(item.page)}$1`);
  }
  if (styleId === 'chicago-notes-bibliography' && item.type === 'webpage') {
    s = chicagoWebpageNote(s, item);
  }
  return s;
}

function chicagoWebpageNote(note: string, item: CslItem): string {
  const org = item.author?.[0]?.literal;
  if (!org || !note.startsWith(`1. ${org}, `)) {
    return note;
  }
  let rest = note.slice(`1. ${org}, `.length);
  const months =
    'January|February|March|April|May|June|July|August|September|' +
    'October|November|December';
  if (new RegExp(`, (${months}) `).test(rest) && !/accessed /i.test(rest)) {
    rest = rest.replace(
      new RegExp(`, (${months}) `),
      `, ${escapeHtmlText(org)}, effective $1 `,
    );
  } else if (/accessed /i.test(rest) && !rest.includes(`, ${org}, accessed`)) {
    rest = rest.replace(',” accessed ', `,” ${escapeHtmlText(org)}, accessed `);
  }
  return `1. ${rest}`;
}

function narrativeCitation(
  styleId: string,
  engine: InstanceType<typeof CSL.Engine>,
  item: CslItem,
): string {
  const authorOnly = decodeCiteprocText(
    engine.makeCitationCluster([{ id: ITEM_ID, 'author-only': 1 }]),
  ).replace(/^\[NO_PRINTED_FORM\]$/, '');
  const yearOnly = decodeCiteprocText(
    engine.makeCitationCluster([{ id: ITEM_ID, 'suppress-author': 1 }]),
  );

  if (styleId === 'modern-language-association') {
    if (!item.author) {
      return mlaTitleFirstCite(item, false);
    }
    return authorOnly.replace(/^\(|\)$/g, '');
  }

  let author = authorOnly;
  if (styleId === 'apa') {
    author = author.replace(/ & /g, ' and ');
  }
  if (styleId === 'cse-name-year') {
    if (!item.author) {
      if (!item.issued) {
        return `${cseShortTitle(item)} [date unknown]`;
      }
      const parts = (item.issued as { 'date-parts'?: number[][] })[
        'date-parts'
      ];
      const year = parts ? String(parts[0][0]) : '';
      return `${cseShortTitle(item)} (${year})`;
    }
    author = cseApplyOrganizationAbbrev(
      author.replace(/et al\./g, 'et al'),
      item,
    );
    return `${author} ${yearOnly}`.replace(/et al\./g, 'et al').trim();
  }

  return `${author} ${yearOnly}`.trim();
}

function mlaTitleFirstCite(item: CslItem, parenthetical: boolean): string {
  const title = escapeHtmlText(item.title || '');
  const formatted = item.type === 'book' ? `<i>${title}</i>` : `“${title}”`;
  return parenthetical ? `(${formatted})` : formatted;
}

function cseShortTitle(item: CslItem): string {
  const title = String(item.title || '');
  const words = title.split(/\s+/);
  const shortened =
    words.length <= 2 ? title : `${words.slice(0, 2).join(' ')} . . .`;
  return escapeHtmlText(shortened);
}

function ieeeNarrative(item: CslItem, numbered: string): string {
  const authors = item.author || [];
  if (authors.length === 0) {
    return numbered;
  }
  const familyOf = (name: CslName): string => {
    const raw = name.literal
      ? name.literal.replace(/\s*\(.*\)\s*/g, '').trim()
      : name.family || '';
    return escapeHtmlText(raw);
  };
  if (authors.length === 1) {
    return `${familyOf(authors[0])} ${numbered}`;
  }
  if (authors.length === 2) {
    return `${familyOf(authors[0])} and ${familyOf(authors[1])} ${numbered}`;
  }
  return `${familyOf(authors[0])} et al. ${numbered}`;
}
